import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type {
  CustomDashAssistantEntityType,
  GlobalAssistantContextPolicy,
  GlobalAssistantEntityType,
  GlobalAssistantSuggestMatch,
} from './global-ai-assistant-api';

export type GlobalAiTranscriptMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'assistant'; kind: 'text'; text: string }
  | {
      id: string;
      role: 'assistant';
      kind: 'clarification';
      text: string;
      clarificationKind?: 'entity_value' | 'entity_scope';
      clarificationOptions?: GlobalAssistantEntityType[];
    }
  | { id: string; role: 'assistant'; kind: 'error'; text: string }
  | {
      id: string;
      role: 'assistant';
      kind: 'page_matches';
      matches: GlobalAssistantSuggestMatch[];
      /**
       * User prompt that produced these matches. Attached at message-level
       * so clicking a chip from a prior prompt (in this session or a
       * hydrated session) resolves filters against THAT prompt, not
       * whatever the latest prompt happens to be.
       * Optional for backwards compatibility with sessions persisted
       * before this field was added.
       */
      prompt?: string;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'single_chart';
      intentSummary?: string;
      /** Factual line shown under the embedded chart (from intent \`chartSubtitle\`). */
      chartSubtitle?: string;
      item: PositionedItem;
      filters?: TalentDiscoveryAiFilterSearchResponseV2;
      entityType: CustomDashAssistantEntityType;
      /**
       * Prompt that produced this chart. Used when opening a chart as a
       * custom page so follow-up filter context stays tied to this chart,
       * not whichever prompt happens to be latest.
       */
      prompt?: string;
      pipelineRequestId?: string;
      standardPagePath?: string;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'non_single_hint';
      text: string;
      offerCustomPage: boolean;
      offerUpdateCurrentPage?: boolean;
      resolvedEntityType?: GlobalAssistantEntityType;
      /**
       * User prompt that produced this hint. Attached at message-level so
       * the create-custom-page button works after the session is
       * hydrated from localStorage (where `lastUserPrompt` is not restored).
       * Optional for backwards compatibility with pre-existing messages.
       */
      prompt?: string;
      /** Router-selected context carryover policy for follow-on calls. */
      contextPolicy?: GlobalAssistantContextPolicy;
    }
  | {
      id: string;
      role: 'assistant';
      kind: 'report_offer';
      reportType: string;
      reportName: string;
      description?: string;
      sampleLink: string;
      confidence: number;
      prompt?: string;
    };

export type GlobalAiInlineChartSpec = {
  messageId: string;
  item: PositionedItem;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
  entityType: CustomDashAssistantEntityType;
  chartSubtitle?: string;
};

export type GlobalAiSingleChartTranscriptMessage = Extract<
  GlobalAiTranscriptMessage,
  { kind: 'single_chart' }
>;

export function toGlobalAiInlineChartSpec(
  msg: GlobalAiSingleChartTranscriptMessage
): GlobalAiInlineChartSpec {
  return {
    messageId: msg.id,
    item: msg.item,
    filters: msg.filters,
    entityType: msg.entityType,
    chartSubtitle: msg.chartSubtitle,
  };
}
